<script lang="ts" setup>
import { ref, computed } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'
const { hasIntermediateAccess } = useAuth();

interface RecipeOverviewItem {
  holeNo: number
  recipeId: number
  recipeName: string
  startTime: Date
  stopTime: Date
  totalTime: string
  totalTimeMs: number
  vcValue: number
  volume: number
  cement: number
  accelerator: number
}

const props = defineProps({
  recipeOverviewData: {
    type: Array as () => RecipeOverviewItem[],
    required: true  
  }
})

const searchQuery = ref('')

const formatDate = (date: any) => {
  if (!date) return "--";
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (date: any) => {
  if (!date) return "--:--:--";
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const formatDurationMs = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

const formatNumber = (num: number, decimals: number = 2) => {
  return num !== undefined ? num.toLocaleString(undefined, { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  }) : '0.00'
}


const filteredRecipes = computed(() => {
  let data = props.recipeOverviewData
  if (!searchQuery.value) return data
  
  const q = searchQuery.value.toLowerCase()
  return data.filter(r => 
    (r.recipeName && r.recipeName.toLowerCase().includes(q)) ||
    (r.holeNo && r.holeNo.toString().includes(q))
  )
})

const recipeStats = computed(() => {
  let data = filteredRecipes.value

  const grouped = new Map();
  for (const item of data) {
      const key = item.recipeId;
      if (!grouped.has(key)) {
          grouped.set(key, {
              recipeId: key,
              recipeName: item.recipeName,
              cement: 0,
              accelerator: 0,
              totalTimeMs: 0
          });
      }
      const group = grouped.get(key);
      group.cement += (item.cement || 0);
      group.accelerator += (item.accelerator || 0);
      
      if (item.totalTimeMs) {
          group.totalTimeMs += item.totalTimeMs;
      }
  }

  return Array.from(grouped.values()).map(item => {
    return {
      name: item.recipeName,
      durationMs: item.totalTimeMs,  
      cement: item.cement,
      accelerator: item.accelerator
    }
  }).sort((a, b) => b.durationMs - a.durationMs)
})

const totalStats = computed(() => {
  const data = filteredRecipes.value
  const totalMs = data.reduce((acc, curr) => acc + curr.totalTimeMs, 0)
  const totalCement = data.reduce((acc, curr) => acc + curr.cement, 0)
  const totalAccelerator = data.reduce((acc, curr) => acc + curr.accelerator, 0)
  return {
    runs: filteredRecipes.value.length,
    duration: formatDurationMs(totalMs),
    cement: formatNumber(totalCement, 1),
    accelerator: formatNumber(totalAccelerator, 1)
  }
})



const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' },
  { title: 'Recipe Name', key: 'recipeName', align: 'start' },
  { title: 'Start Time', key: 'startTime', align: 'start' },
  { title: 'Stop Time', key: 'stopTime', align: 'start' },
  { title: 'Total Time', key: 'totalTime', align: 'end' },
  { title: 'VC Value', key: 'vcValue', align: 'end' },
  { title: 'Cement (kg)', key: 'cement', align: 'end' },
  { title: 'Accel. (kg)', key: 'accelerator', align: 'end' },
] as const;
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else class="pa-4 pa-md-6">
    <v-row class="mb-6">
      <!-- Total Usage -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-6">
            <v-row dense>
              <v-col cols="6" sm="6">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Runs</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.runs }}</div>
              </v-col>
              <v-col cols="6" sm="6">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Duration</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.duration }}</div>
              </v-col>
              <v-col cols="6" sm="6" class="mt-4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Cement</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.cement }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="6" class="mt-4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Total Accelerator</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ totalStats.accelerator }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Time per Recipe -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Consumption per Recipe
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 300px; overflow-y: auto;">
            <div v-for="r in recipeStats" :key="r.name" class="mb-3 pa-3 bg-grey-lighten-5 rounded border">
              <div class="text-subtitle-2 font-weight-bold mb-2 text-blue-grey-darken-3">{{ r.name }}</div>
              <div class="d-flex justify-space-between">
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Cement</span>
                  <span class="font-weight-bold font-monospace">{{ formatNumber(r.cement, 1) }} kg</span>
                </div>
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Accel.</span>
                  <span class="font-weight-bold font-monospace">{{ formatNumber(r.accelerator, 1) }} kg</span>
                </div>
                <div class="d-flex flex-column align-end">
                  <span class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Time</span>
                  <span class="font-weight-bold font-monospace">{{ formatDurationMs(r.durationMs) }}</span>
                </div>
              </div>
            </div>
            <div v-if="recipeStats.length === 0" class="text-center text-medium-emphasis font-italic py-2">
              No recipe data available
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recipe Overview Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">Recipe Overview ({{ filteredRecipes.length }})</span>
        <div class="d-flex align-center gap-4 flex-wrap">
          <v-text-field
            v-model="searchQuery"
            placeholder="Filter by Recipe or Hole..."
            prepend-inner-icon="mdi-magnify"
            density="compact"
            variant="outlined"
            hide-details
            bg-color="white"
            style="min-width: 250px;"
          ></v-text-field>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="filteredRecipes"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <template v-slot:item.recipeName="{ item }">
          <div class="d-flex flex-column py-1">
            <span class="font-weight-bold text-body-2">{{ item.recipeName || 'Unknown' }}</span>
            <v-chip size="x-small" color="grey-darken-3" variant="flat" class="font-weight-bold align-self-start mt-1">
              ID: {{ item.recipeId }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.startTime="{ item }">
          <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.startTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.startTime) }}</span>
          </div>
        </template>

        <template v-slot:item.stopTime="{ item }">
          <div class="d-flex flex-column py-1">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.stopTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.stopTime) }}</span>
          </div>
        </template>

        <template v-slot:item.totalTime="{ item }">
          <div class="d-flex align-center justify-end font-weight-bold text-high-emphasis">
            <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
            {{ item.totalTime }}
          </div>
        </template>

        <template v-slot:item.cement="{ item }"><span class="font-monospace">{{ formatNumber(item.cement, 1) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ formatNumber(item.accelerator, 1) }}</span></template>
        <template v-slot:item.vcValue="{ item }"><span class="font-monospace">{{ item.vcValue }}</span></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.gap-4 {
  gap: 1rem;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>